import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type {
  AmendmentEffect,
  AmendmentLink,
  Legislation,
  LegislationDetail,
  LegislationVersion,
  Page,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const AMENDMENT_EFFECTS: AmendmentEffect[] = ['AMENDS', 'REPEALS', 'REPEALS_AND_REPLACES', 'INSERTS', 'SUBSTITUTES'];

const TABS = [
  { key: 'text', label: 'Consolidated text' },
  { key: 'versions', label: 'Version history' },
  { key: 'amendments', label: 'Amendment linkage' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/**
 * Public legislation detail — GET /api/v1/m38/legislation/:id, no auth
 * required. `at` supports "law as at date X" point-in-time views. A signed-in
 * LAW_REVISION_COMMISSIONER additionally sees consolidation/amendment/repeal
 * actions (all gated server-side too — these buttons are convenience, not
 * the real access control).
 */
export function LawDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('LAW_REVISION_COMMISSIONER') ?? false;
  const [at, setAt] = useState('');
  const [tab, setTab] = useState<TabKey>('text');
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [showAmendment, setShowAmendment] = useState(false);
  const [repealBusy, setRepealBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const path = `/api/v1/m38/legislation/${id}${at ? `?at=${at}` : ''}`;
  const detailQ = useApiQuery<LegislationDetail>(id ? path : null);
  const versionsQ = useApiQuery<{ items: LegislationVersion[] }>(tab === 'versions' && id ? `/api/v1/m38/legislation/${id}/versions` : null);

  const repeal = async () => {
    if (!confirm('Repeal this instrument in whole? This closes its current version and cannot be undone.')) return;
    setRepealBusy(true);
    setActionError(null);
    try {
      await api.post(`/api/v1/m38/legislation/${id}/repeal`, { repealedDate: new Date().toISOString() });
      detailQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Repeal failed');
    } finally {
      setRepealBusy(false);
    }
  };

  if (detailQ.loading) return <Spinner label="Loading legislation…" />;
  if (detailQ.error) return <ErrorNote message={detailQ.error} />;
  const l = detailQ.data;
  if (!l) return null;

  return (
    <div>
      <PageHeader
        title={l.shortTitle}
        subtitle={`${l.title} — ${l.instrumentNumber}${l.chapterNo ? ` (${l.chapterNo})` : ''}`}
        actions={
          <>
            <StatusBadge status={l.status} />
            <label className="ml-2 flex items-center gap-2 text-sm">
              <span className="font-semibold text-slate-500">As at</span>
              <input type="date" className="field-input !w-auto" value={at} onChange={(e) => setAt(e.target.value)} />
            </label>
          </>
        }
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {canManage ? (
        <div className="mb-5 flex flex-wrap gap-2">
          <button className="btn-primary" onClick={() => setShowNewVersion(true)} disabled={l.status === 'REPEALED'}>
            + Publish consolidated version
          </button>
          <button className="btn-secondary" onClick={() => setShowAmendment(true)}>
            + Record amendment link
          </button>
          <button className="btn-danger" onClick={repeal} disabled={repealBusy || l.status === 'REPEALED'}>
            {repealBusy ? 'Repealing…' : 'Repeal instrument'}
          </button>
        </div>
      ) : null}

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === 'text' ? (
        l.version ? (
          <div className="space-y-4">
            <div className="card p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-bold text-slate-700">
                  Version {l.version.versionNumber} — <StatusBadge status={l.version.status} />
                </div>
                <div className="text-xs text-slate-500">
                  Effective {formatDate(l.version.effectiveFrom)}
                  {l.version.effectiveTo ? ` – ${formatDate(l.version.effectiveTo)}` : ' – present'}
                </div>
              </div>
              {l.version.consolidationNote ? <p className="mt-2 text-xs text-slate-500">{l.version.consolidationNote}</p> : null}
              {l.version.docVaultSerial ? (
                <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
                  DocVault serial <code>{l.version.docVaultSerial}</code>
                  {l.version.docVaultVerifyUrl ? (
                    <>
                      {' '}
                      ·{' '}
                      <a href={l.version.docVaultVerifyUrl} target="_blank" rel="noreferrer" className="text-brand-700 underline">
                        verify ↗
                      </a>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>

            {l.provisions.length === 0 ? (
              <EmptyState title="No provisions recorded for this version" />
            ) : (
              <div className="space-y-2.5">
                {l.provisions.map((p) => (
                  <div key={p.id} className="card p-4">
                    <div className="text-sm font-bold text-slate-800">
                      Section {p.sectionNumber}
                      {p.heading ? <span className="font-normal text-slate-500"> — {p.heading}</span> : null}
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-700">{p.bodyText}</p>
                    <code className="mt-1.5 block text-[10px] text-slate-300">{p.id}</code>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <EmptyState
            title={at ? `No consolidated text was in force as at ${formatDate(at)}` : 'No consolidated version has been published yet'}
            hint={canManage ? 'Use "Publish consolidated version" above to add the first one.' : undefined}
          />
        )
      ) : null}

      {tab === 'versions' ? (
        versionsQ.loading ? (
          <Spinner />
        ) : versionsQ.error ? (
          <ErrorNote message={versionsQ.error} />
        ) : (versionsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No versions yet" />
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Version</th>
                  <th className="table-head">Status</th>
                  <th className="table-head">Effective from</th>
                  <th className="table-head">Effective to</th>
                  <th className="table-head">Note</th>
                </tr>
              </thead>
              <tbody>
                {versionsQ.data!.items.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">v{v.versionNumber}</td>
                    <td className="table-cell">
                      <StatusBadge status={v.status} />
                    </td>
                    <td className="table-cell text-xs">{formatDate(v.effectiveFrom)}</td>
                    <td className="table-cell text-xs">{v.effectiveTo ? formatDate(v.effectiveTo) : 'present'}</td>
                    <td className="table-cell text-xs text-slate-500">{v.consolidationNote ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {tab === 'amendments' ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <div>
            <h2 className="mb-2 text-sm font-bold text-slate-700">Amendments this instrument made to others</h2>
            <AmendmentList items={l.amendmentsMade} otherKey="targetLegislationId" />
          </div>
          <div>
            <h2 className="mb-2 text-sm font-bold text-slate-700">Amendments received from other instruments</h2>
            <AmendmentList items={l.amendmentsReceived} otherKey="amendingLegislationId" />
          </div>
        </div>
      ) : null}

      {showNewVersion ? (
        <NewVersionModal
          legislationId={l.id}
          onClose={() => setShowNewVersion(false)}
          onDone={() => {
            setShowNewVersion(false);
            detailQ.reload();
          }}
        />
      ) : null}
      {showAmendment ? (
        <AmendmentModal
          legislationId={l.id}
          onClose={() => setShowAmendment(false)}
          onDone={() => {
            setShowAmendment(false);
            detailQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AmendmentList({ items, otherKey }: { items: AmendmentLink[]; otherKey: 'targetLegislationId' | 'amendingLegislationId' }) {
  if (items.length === 0) return <EmptyState title="None recorded" />;
  return (
    <ul className="space-y-2">
      {items.map((a) => (
        <li key={a.id} className="card p-3.5">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{a.effect.replaceAll('_', ' ')}</span>
            <span className="text-xs text-slate-400">{formatDate(a.effectiveFrom)}</span>
          </div>
          <p className="mt-1 text-sm text-slate-700">{a.description}</p>
          <Link to={`/law/${a[otherKey]}`} className="mt-1 inline-block text-xs text-brand-700 hover:underline">
            View {otherKey === 'targetLegislationId' ? 'target' : 'amending'} instrument →
          </Link>
        </li>
      ))}
    </ul>
  );
}

function NewVersionModal({ legislationId, onClose, onDone }: { legislationId: string; onClose: () => void; onDone: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [consolidationNote, setConsolidationNote] = useState('');
  const [sourceGazetteIssueId, setSourceGazetteIssueId] = useState('');
  const [provisions, setProvisions] = useState([{ sectionNumber: '1', heading: '', bodyText: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addRow = () => setProvisions((rows) => [...rows, { sectionNumber: '', heading: '', bodyText: '' }]);
  const removeRow = (i: number) => setProvisions((rows) => rows.filter((_, idx) => idx !== i));
  const updateRow = (i: number, field: 'sectionNumber' | 'heading' | 'bodyText', value: string) =>
    setProvisions((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/legislation/${legislationId}/versions`, {
        effectiveFrom: new Date(`${effectiveFrom}T00:00:00.000Z`).toISOString(),
        consolidationNote: consolidationNote || undefined,
        sourceGazetteIssueId: sourceGazetteIssueId || undefined,
        provisions: provisions.map((p, idx) => ({
          sectionNumber: p.sectionNumber,
          heading: p.heading || undefined,
          bodyText: p.bodyText,
          orderIndex: idx,
        })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to publish version');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    effectiveFrom.length > 0 &&
    provisions.length > 0 &&
    provisions.every((p) => p.sectionNumber.trim().length > 0 && p.bodyText.trim().length > 0);

  return (
    <Modal title="Publish new consolidated version" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Effective from">
            <input type="date" className="field-input" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Source gazette issue id (optional)">
            <input className="field-input" value={sourceGazetteIssueId} onChange={(e) => setSourceGazetteIssueId(e.target.value)} placeholder="gazette issue id…" />
          </Field>
        </div>
        <Field label="Consolidation note (optional)">
          <input className="field-input" value={consolidationNote} onChange={(e) => setConsolidationNote(e.target.value)} placeholder="e.g. Incorporates Act 12 of 2025 amendments" />
        </Field>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="field-label !mb-0">Provisions (reading order)</span>
            <button className="text-xs font-semibold text-brand-700 hover:underline" onClick={addRow}>
              + Add section
            </button>
          </div>
          <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
            {provisions.map((p, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2.5">
                <div className="mb-1.5 flex gap-2">
                  <input
                    className="field-input !w-24"
                    value={p.sectionNumber}
                    onChange={(e) => updateRow(i, 'sectionNumber', e.target.value)}
                    placeholder="s. no."
                  />
                  <input
                    className="field-input flex-1"
                    value={p.heading}
                    onChange={(e) => updateRow(i, 'heading', e.target.value)}
                    placeholder="Heading (optional)"
                  />
                  {provisions.length > 1 ? (
                    <button className="shrink-0 text-slate-400 hover:text-rose-600" onClick={() => removeRow(i)} aria-label="Remove">
                      ✕
                    </button>
                  ) : null}
                </div>
                <textarea
                  className="field-input"
                  rows={2}
                  value={p.bodyText}
                  onChange={(e) => updateRow(i, 'bodyText', e.target.value)}
                  placeholder="Section text…"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Publishing…' : 'Publish version'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AmendmentModal({ legislationId, onClose, onDone }: { legislationId: string; onClose: () => void; onDone: () => void }) {
  const [targetLegislationId, setTargetLegislationId] = useState('');
  const [effect, setEffect] = useState<AmendmentEffect>('AMENDS');
  const [description, setDescription] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [amendingProvisionId, setAmendingProvisionId] = useState('');
  const [targetProvisionId, setTargetProvisionId] = useState('');
  const [candidates, setCandidates] = useState<Legislation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<Page<Legislation>>('/api/v1/m38/legislation?limit=200')
      .then((p) => setCandidates(p.items.filter((c) => c.id !== legislationId)))
      .catch(() => setCandidates([]));
  }, [legislationId]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/legislation/${legislationId}/amendments`, {
        targetLegislationId,
        effect,
        description,
        effectiveFrom: new Date(`${effectiveFrom}T00:00:00.000Z`).toISOString(),
        amendingProvisionId: amendingProvisionId || undefined,
        targetProvisionId: targetProvisionId || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record amendment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record amendment link" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">This instrument is recorded as the one doing the amending.</p>
        <Field label="Target instrument (being amended/repealed)">
          <select className="field-input" value={targetLegislationId} onChange={(e) => setTargetLegislationId(e.target.value)}>
            <option value="">Select…</option>
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.shortTitle} ({c.instrumentNumber})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Effect">
            <select className="field-input" value={effect} onChange={(e) => setEffect(e.target.value as AmendmentEffect)}>
              {AMENDMENT_EFFECTS.map((e) => (
                <option key={e} value={e}>
                  {e.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Effective from">
            <input type="date" className="field-input" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
        </div>
        <Field label="Description">
          <input className="field-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. s.4 substituted" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amending provision id (optional)">
            <input className="field-input" value={amendingProvisionId} onChange={(e) => setAmendingProvisionId(e.target.value)} placeholder="paste from this Act's own section list" />
          </Field>
          <Field label="Target provision id (optional)">
            <input className="field-input" value={targetProvisionId} onChange={(e) => setTargetProvisionId(e.target.value)} placeholder="paste from the target's section list" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !targetLegislationId || description.trim().length < 3 || !effectiveFrom}
          >
            {busy ? 'Recording…' : 'Record amendment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
